"use client";

import type { RefObject } from "react";
import { useLayoutEffect, useRef, useState } from "react";

import { HourglassMark } from "@/components/HourglassMark";

/** Session-scoped: one cinematic launch per tab load (replay bumps `replayKey` + clears this). */
export const DESK_LAUNCH_SESSION_KEY = "ed_desk_launch_v3_done";

/** Centre hero + nav target — matches dashboard header wordmark hourglass. */
const MARK_PX = 28;
const MARK_H = Math.round((MARK_PX * 48) / 40);
/** Slightly larger on first appearance, eases to nav scale during flight. */
const CENTER_SCALE = 1.45;

const HERO_HG_SELECTOR = "[data-ed-wordmark-x-anchor]";

/** Smooth forward-only revolutions (no unwind). */
const INTRO_SPIN = {
  ms: 1100,
  revs: 3,
} as const;

const HOLD = {
  measureMs: 0,
  revealMs: 280,
  minWordmarkMs: 2000,
} as const;

const REVEAL_INITIAL = { opacity: 0.18, blurPx: 6, scale: 0.94 } as const;

const FINALE = {
  flightMs: 980,
  landMs: 220,
  /** Shell fades; “E” / “piryDesk” reveal around the landed hourglass. */
  revealTextMs: 620,
} as const;

const FINALE_TOTAL_MS = FINALE.flightMs + FINALE.landMs + FINALE.revealTextMs;

export function loadAppData(): Promise<void> {
  return Promise.resolve();
}

function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

function snapUprightDeg(deg: number): number {
  return deg - normalizeDeg(deg);
}

function easeOutQuint(t: number): number {
  const u = clamp01(t);
  return 1 - (1 - u) ** 5;
}

function easeInOutQuint(t: number): number {
  const u = clamp01(t);
  return u < 0.5 ? 16 * u * u * u * u * u : 1 - (-2 * u + 2) ** 5 / 2;
}

function easeInOutSine(t: number): number {
  const u = clamp01(t);
  return -(Math.cos(Math.PI * u) - 1) / 2;
}

function easeOutBack(t: number, s = 1.35): number {
  const u = clamp01(t);
  const x = u - 1;
  return 1 + (s + 1) * x * x * x + s * x * x;
}

function quadBezier(p0: { x: number; y: number }, p1: { x: number; y: number }, p2: { x: number; y: number }, t: number): { x: number; y: number } {
  const u = clamp01(t);
  const omt = 1 - u;
  return {
    x: omt * omt * p0.x + 2 * omt * u * p1.x + u * u * p2.x,
    y: omt * omt * p0.y + 2 * omt * u * p1.y + u * u * p2.y,
  };
}

type Rect = Pick<DOMRect, "left" | "top" | "width" | "height">;

function readRect(el: Element | null): Rect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 1 && r.height < 1) return null;
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

function center(r: Rect): { x: number; y: number } {
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

export type DeskLaunchPhase =
  | "measure"
  | "reveal"
  | "hold"
  | "waiting_data"
  | "finale_flight"
  | "finale_land"
  | "finale_reveal"
  | "complete";

export type DeskLaunchHooks = {
  onPhase?: (phase: DeskLaunchPhase, ctx: { elapsedMs: number; progress01: number }) => void;
  onProgress?: (progress01: number, elapsedMs: number) => void;
  track?: (event: string, payload?: Record<string, unknown>) => void;
};

export class RafTimeline {
  private readonly jobs: { t: number; fn: () => void }[] = [];
  private rafId = 0;
  private t0 = 0;
  private jobIdx = 0;
  private cancelled = false;
  private tick?: (elapsed: number, now: number) => void;

  at(tMs: number, fn: () => void): this {
    this.jobs.push({ t: tMs, fn });
    this.jobs.sort((a, b) => a.t - b.t);
    return this;
  }

  onFrame(fn: (elapsed: number, now: number) => void): this {
    this.tick = fn;
    return this;
  }

  start(): void {
    this.cancelled = false;
    this.jobIdx = 0;
    this.t0 = performance.now();
    const step = (now: number) => {
      if (this.cancelled) return;
      const elapsed = now - this.t0;
      while (this.jobIdx < this.jobs.length && elapsed >= this.jobs[this.jobIdx].t) {
        try {
          this.jobs[this.jobIdx].fn();
        } catch {
          /* non-fatal */
        }
        this.jobIdx += 1;
      }
      this.tick?.(elapsed, now);
      if (!this.cancelled) {
        this.rafId = requestAnimationFrame(step);
      }
    };
    this.rafId = requestAnimationFrame(step);
  }

  cancel(): void {
    this.cancelled = true;
    cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }
}

export type DeskLaunchSequenceProps = {
  isDarkMode: boolean;
  navHourglassAnchorRef: RefObject<HTMLElement | null>;
  navLogoMountRef?: RefObject<HTMLElement | null>;
  dataReady: boolean;
  onComplete: () => void;
  replayKey?: number;
  hooks?: DeskLaunchHooks;
  completeAnnouncement?: string;
};

export function DeskLaunchSequence({
  isDarkMode,
  navHourglassAnchorRef,
  navLogoMountRef,
  dataReady,
  onComplete,
  replayKey = 0,
  hooks,
  completeAnnouncement = "Dashboard ready",
}: DeskLaunchSequenceProps) {
  const heroAnchorRef = useRef<HTMLSpanElement | null>(null);
  const travelRef = useRef<HTMLDivElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const glowRef = useRef<HTMLDivElement | null>(null);
  const spinDegRef = useRef(0);

  const [liveText, setLiveText] = useState("");

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const hooksRef = useRef(hooks);
  hooksRef.current = hooks;
  const dataReadyRef = useRef(dataReady);
  dataReadyRef.current = dataReady;

  useLayoutEffect(() => {
    let finished = false;
    let holdRaf = 0;
    let finaleTl: RafTimeline | null = null;

    const navRef = navHourglassAnchorRef;

    const gate = { opened: false, minHold: false, mockBoot: false };
    const geom = { startRect: null as Rect | null, lastNav: null as { x: number; y: number } | null };
    const kine = { spinDeg: 0, scale: CENTER_SCALE };

    let lastPhase: DeskLaunchPhase | "" = "";
    const emitPhase = (phase: DeskLaunchPhase, elapsedMs: number, denom = HOLD.measureMs + HOLD.revealMs + HOLD.minWordmarkMs + FINALE_TOTAL_MS) => {
      if (phase === lastPhase) return;
      lastPhase = phase;
      const progress01 = clamp01(elapsedMs / Math.max(1, denom));
      hooksRef.current?.onPhase?.(phase, { elapsedMs, progress01 });
      hooksRef.current?.track?.("desk_launch_phase", { phase, elapsedMs, progress01 });
    };

    const applySpin = (deg: number, scale: number, offset?: { x: number; y: number }) => {
      spinDegRef.current = deg;
      kine.spinDeg = deg;
      kine.scale = scale;
      const travel = travelRef.current;
      const hgWrap = travel?.querySelector(HERO_HG_SELECTOR) as HTMLElement | null;
      if (hgWrap) {
        hgWrap.style.transform = `rotate(${deg}deg)`;
        hgWrap.style.transformOrigin = "50% 50%";
        hgWrap.style.willChange = "transform";
      }
      if (travel) {
        const shift =
          offset && (Math.abs(offset.x) > 0.05 || Math.abs(offset.y) > 0.05)
            ? `translate(${offset.x}px, ${offset.y}px) `
            : "";
        travel.style.transform = `${shift}scale(${scale})`;
      }
    };

    const tryOpenGate = () => {
      if (finished || gate.opened) return;
      if (gate.minHold && gate.mockBoot && dataReadyRef.current) {
        gate.opened = true;
        cancelAnimationFrame(holdRaf);
        holdRaf = 0;
        startFinale();
      }
    };

    const finish = () => {
      if (finished) return;
      finished = true;
      cancelAnimationFrame(holdRaf);
      finaleTl?.cancel();
      finaleTl = null;

      const travel = travelRef.current;
      if (travel) {
        travel.style.cssText = "";
      }
      const hgWrap = travel?.querySelector(HERO_HG_SELECTOR) as HTMLElement | null;
      if (hgWrap instanceof HTMLElement) {
        hgWrap.style.transform = "";
        hgWrap.style.transformOrigin = "";
        hgWrap.style.willChange = "";
      }

      const shell = shellRef.current;
      if (shell) shell.style.opacity = "";
      const glow = glowRef.current;
      if (glow) glow.style.opacity = "";

      const navLogo = navLogoMountRef?.current;
      if (navLogo) {
        navLogo.style.removeProperty("--ed-launch-gloss-shift");
        const glosses = navLogo.querySelectorAll(".ed-wordmark-composed__gloss");
        glosses.forEach((el) => {
          if (el instanceof HTMLElement) {
            el.style.opacity = "";
            el.style.transform = "";
          }
        });
        const navHg = navLogo.querySelector(".ed-wordmark-composed__hg");
        if (navHg instanceof HTMLElement) navHg.style.opacity = "";
      }

      try {
        document.documentElement.classList.remove("ed-desk-launch-active", "ed-desk-launch-handoff");
      } catch {
        /* ignore */
      }

      emitPhase("complete", HOLD.measureMs + HOLD.revealMs + HOLD.minWordmarkMs + FINALE_TOTAL_MS, FINALE_TOTAL_MS);
      try {
        sessionStorage.setItem(DESK_LAUNCH_SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
      onCompleteRef.current();
    };

    const tReveal0 = HOLD.measureMs;
    const tReveal1 = tReveal0 + HOLD.revealMs;
    const minHoldEnd = tReveal1 + HOLD.minWordmarkMs;

    const holdLoop = (now: number) => {
      if (finished || gate.opened) return;
      const elapsed = now - t0;
      const motionT = Math.max(0, elapsed - tReveal0);

      hooksRef.current?.onProgress?.(clamp01(elapsed / (minHoldEnd + FINALE_TOTAL_MS)), elapsed);

      const travel = travelRef.current;
      const glow = glowRef.current;

      if (motionT > 0) {
        const spinU = clamp01(motionT / INTRO_SPIN.ms);
        const holdExtra = Math.max(0, motionT - INTRO_SPIN.ms);
        const deg = easeOutQuint(spinU) * INTRO_SPIN.revs * 360 + holdExtra * 0.14;
        applySpin(deg, CENTER_SCALE);
      }

      if (elapsed < tReveal0) {
        /* measure */
      } else if (elapsed <= tReveal1) {
        emitPhase("reveal", elapsed);
        if (travel) {
          const u = easeOutQuint(motionT / HOLD.revealMs);
          const opacity = REVEAL_INITIAL.opacity + (1 - REVEAL_INITIAL.opacity) * u;
          travel.style.opacity = String(opacity);
          const blur = REVEAL_INITIAL.blurPx * (1 - u);
          travel.style.filter = blur > 0.04 ? `blur(${blur}px)` : "none";
        }
        if (glow) {
          const u = easeOutQuint(motionT / HOLD.revealMs);
          glow.style.opacity = String(0.28 + 0.22 * u);
        }
      } else {
        if (elapsed >= tReveal1 && lastPhase !== "hold" && lastPhase !== "waiting_data") {
          emitPhase("hold", elapsed);
        }
        const wait = elapsed - tReveal1;
        if (travel && travel.style.position !== "fixed") {
          travel.style.opacity = "1";
          travel.style.filter = "none";
          const breathe = Math.sin(wait * 0.0022) * 0.008;
          const lift = Math.sin(wait * 0.0016) * 2.2;
          const scale = CENTER_SCALE * (1 + breathe);
          applySpin(spinDegRef.current, scale, { x: 0, y: lift });
        }
        if (glow) {
          const pulse = 0.55 + 0.45 * Math.sin(wait * 0.0028);
          glow.style.opacity = String(0.35 + 0.25 * pulse);
        }

        if (elapsed >= minHoldEnd && !gate.minHold) {
          gate.minHold = true;
          hooksRef.current?.track?.("desk_launch_min_hold_complete", { elapsedMs: elapsed });
          tryOpenGate();
        }

        if (gate.minHold && (!dataReadyRef.current || !gate.mockBoot)) {
          if (lastPhase !== "waiting_data") emitPhase("waiting_data", elapsed);
        }

        tryOpenGate();
      }

      if (!finished && !gate.opened) {
        holdRaf = requestAnimationFrame(holdLoop);
      }
    };

    function pinTravelFixed(at: { x: number; y: number }, scale: number, deg: number) {
      const travel = travelRef.current;
      if (!travel) return;
      travel.style.position = "fixed";
      travel.style.zIndex = "10062";
      travel.style.margin = "0";
      travel.style.left = `${at.x - (MARK_PX * scale) / 2}px`;
      travel.style.top = `${at.y - (MARK_H * scale) / 2}px`;
      travel.style.width = `${MARK_PX}px`;
      travel.style.height = `${MARK_H}px`;
      travel.style.opacity = "1";
      travel.style.filter = "none";
      travel.style.pointerEvents = "none";
      travel.style.willChange = "transform, left, top, filter";
      applySpin(deg, scale);
    }

    function startFinale() {
      const start = geom.startRect;
      const travel = travelRef.current;
      if (!start || !travel) {
        hooksRef.current?.track?.("desk_launch_aborted", { reason: "missing_geometry" });
        finish();
        return;
      }

      const travelRect = readRect(heroAnchorRef.current) ?? start;
      const origin = center(travelRect);
      const spinFlight0 = spinDegRef.current;
      pinTravelFixed(origin, CENTER_SCALE, spinFlight0);

      const flightStart = { x: origin.x, y: origin.y };
      let handedOff = false;

      finaleTl = new RafTimeline();
      const tF1 = FINALE.flightMs;
      const tL1 = tF1 + FINALE.landMs;
      const tEnd = tL1 + FINALE.revealTextMs;

      finaleTl.onFrame((fe) => {
        if (finished) return;
        const navR = readRect(navRef.current);
        if (navR) geom.lastNav = center(navR);
        const p2 = geom.lastNav ?? center(start);

        if (fe <= tF1) {
          emitPhase("finale_flight", fe, FINALE_TOTAL_MS);
          const u = easeInOutSine(fe / FINALE.flightMs);
          const p0 = { x: flightStart.x, y: flightStart.y };
          const dx = p2.x - p0.x;
          const dy = p2.y - p0.y;
          const len = Math.hypot(dx, dy) || 1;
          const nx = -dy / len;
          const ny = dx / len;
          const lift = Math.min(118, Math.max(32, len * 0.22));
          const p1 = { x: (p0.x + p2.x) / 2 + nx * lift, y: (p0.y + p2.y) / 2 + ny * lift };
          const pt = quadBezier(p0, p1, p2, u);
          const scale = CENTER_SCALE + (1 - CENTER_SCALE) * u;
          const rawSpin = spinFlight0 + u * 720;
          const snapBlend = easeInOutQuint(Math.max(0, (u - 0.72) / 0.28));
          const snapped = snapUprightDeg(rawSpin);
          const deg = rawSpin + (snapped - rawSpin) * snapBlend;
          pinTravelFixed(pt, scale, deg);
        } else if (fe <= tL1) {
          emitPhase("finale_land", fe, FINALE_TOTAL_MS);
          const endR = readRect(navRef.current);
          if (!endR) return;
          const c = center(endR);
          const u = easeOutBack((fe - tF1) / FINALE.landMs);
          const bounce = 1 + 0.06 * (1 - u) * Math.sin(u * Math.PI);
          const scale = bounce;
          const upright = snapUprightDeg(kine.spinDeg);
          const deg = upright * (1 - easeOutBack(u));
          pinTravelFixed(c, scale, deg);
        } else if (fe <= tEnd) {
          emitPhase("finale_reveal", fe, FINALE_TOTAL_MS);
          if (completeAnnouncement) setLiveText(completeAnnouncement);

          const u = easeOutQuint((fe - tL1) / FINALE.revealTextMs);
          const shell = shellRef.current;
          if (shell) shell.style.opacity = String(1 - u);

          const endR = readRect(navRef.current);
          if (endR) {
            const c = center(endR);
            pinTravelFixed(c, 1, 0);
          }

          const navLogo = navLogoMountRef?.current;
          if (navLogo) {
            const row = navLogo.querySelector(".ed-wordmark-composed__row");
            const glossE = row?.querySelector(".ed-wordmark-composed__gloss:first-of-type");
            const glossDesk = row?.querySelector(".ed-wordmark-composed__gloss:last-of-type");
            const textU = easeOutQuint(Math.max(0, (u - 0.12) / 0.88));
            const shift = 11 * (1 - textU);
            if (glossE instanceof HTMLElement) {
              glossE.style.opacity = String(textU);
              glossE.style.transform = `translateX(${-shift}px)`;
            }
            if (glossDesk instanceof HTMLElement) {
              glossDesk.style.opacity = String(textU);
              glossDesk.style.transform = `translateX(${shift}px)`;
            }

            if (u >= 0.88 && !handedOff) {
              handedOff = true;
              try {
                document.documentElement.classList.add("ed-desk-launch-handoff");
                document.documentElement.classList.remove("ed-desk-launch-active");
              } catch {
                /* ignore */
              }
              if (travel) travel.style.visibility = "hidden";
              const navHg = navLogo.querySelector(".ed-wordmark-composed__hg");
              if (navHg instanceof HTMLElement) navHg.style.opacity = "1";
            }
          }
        }

        if (fe >= tEnd) {
          finaleTl?.cancel();
          finaleTl = null;
          finish();
        }
      });

      finaleTl.start();
    }

    const t0 = performance.now();

    let measureAttempts = 0;
    const measureJob = () => {
      emitPhase("measure", HOLD.measureMs);
      const s = readRect(heroAnchorRef.current);
      const e = readRect(navRef.current);
      if (!s || !e) {
        if (measureAttempts < 24) {
          measureAttempts += 1;
          requestAnimationFrame(measureJob);
          return;
        }
        hooksRef.current?.track?.("desk_launch_aborted", { reason: "missing_anchor" });
        finish();
        return;
      }
      geom.startRect = s;
      geom.lastNav = center(e);
    };

    if (HOLD.measureMs <= 0) {
      measureJob();
    } else {
      window.setTimeout(measureJob, HOLD.measureMs);
    }

    let mockCancelled = false;
    void loadAppData().then(() => {
      if (mockCancelled || finished) return;
      gate.mockBoot = true;
      hooksRef.current?.track?.("desk_launch_mock_boot_ready", {});
      tryOpenGate();
    });

    holdRaf = requestAnimationFrame(holdLoop);

    return () => {
      finished = true;
      mockCancelled = true;
      cancelAnimationFrame(holdRaf);
      finaleTl?.cancel();
      const travel = travelRef.current;
      if (travel) travel.style.cssText = "";
      try {
        document.documentElement.classList.remove("ed-desk-launch-active", "ed-desk-launch-handoff");
      } catch {
        /* ignore */
      }
    };
  }, [completeAnnouncement, isDarkMode, navHourglassAnchorRef, navLogoMountRef, replayKey]);

  const fieldClass = isDarkMode
    ? "bg-[#0b1220] [background-image:radial-gradient(ellipse_at_50%_44%,rgba(34,211,238,0.18)_0%,rgba(15,23,42,0.16)_40%,transparent_70%)]"
    : "bg-slate-50 [background-image:radial-gradient(ellipse_at_50%_44%,rgba(14,165,233,0.15)_0%,rgba(248,250,252,0.52)_44%,transparent_70%)]";

  return (
    <>
      <div className="ed-visually-hidden" aria-live="polite" aria-atomic="true">
        {liveText}
      </div>
      <div
        ref={shellRef}
        className={`fixed inset-0 z-[10060] flex flex-col items-center justify-center ${fieldClass}`}
        style={{ opacity: 1, pointerEvents: "auto" }}
        role="presentation"
        aria-hidden
      >
        <div
          ref={glowRef}
          className="pointer-events-none absolute inset-0 rounded-none"
          style={{
            opacity: 0.45,
            background: isDarkMode
              ? "radial-gradient(ellipse 55% 48% at 50% 42%, rgba(34,211,238,0.22) 0%, transparent 62%)"
              : "radial-gradient(ellipse 55% 48% at 50% 42%, rgba(14,165,233,0.18) 0%, transparent 62%)",
          }}
          aria-hidden
        />
        <div
          ref={travelRef}
          className="ed-desk-launch-travel relative z-[1] flex items-center justify-center"
          style={
            HOLD.revealMs <= 0
              ? { opacity: 1, filter: "none" }
              : { opacity: REVEAL_INITIAL.opacity, filter: `blur(${REVEAL_INITIAL.blurPx}px)` }
          }
        >
          <span ref={heroAnchorRef} className="inline-flex shrink-0" data-ed-wordmark-x-anchor="">
            <HourglassMark size={MARK_PX} premiumDark={isDarkMode} withSand className="ed-wordmark-composed__hg" title="" />
          </span>
        </div>
      </div>
    </>
  );
}
